// src/services/openai.ts

import { Surgery, Patient, Doctor, PatientSpecialConditions, Section } from '../types';
import { WavRecorder, WavStreamPlayer, RealtimeAPI } from './audioService';
import { tt, Language } from '../i18n';

// gpt-realtime-2 의 최신 자연 음성 (marin/cedar). 이전 alloy/ash 보다 훨씬 사람 같음.
type VoiceType = 'cedar' | 'marin';

const VOICE_MAPPING = {
  '남성': 'cedar' as VoiceType,
  '여성': 'marin' as VoiceType
};

const DEFAULT_VOICE: VoiceType = 'marin';

const MALE_VOICES: VoiceType[] = ['cedar'];

export class OpenAIRealtimeService {
  private client: RealtimeAPI;
  private wavRecorder: WavRecorder;
  private wavStreamPlayer: WavStreamPlayer;
  private surgery: Surgery | null = null;
  private patient: Patient | null = null;
  private doctor: Doctor | null = null;
  private specialConditions: PatientSpecialConditions | null = null;
  private currentVoice: VoiceType = DEFAULT_VOICE;
  private isConnecting: boolean = false;
  private isFullyConnected: boolean = false;
  private messageId: number = 0;
  private pendingMessageId: string | null = null;
  private isRecording: boolean = false;
  private onMessageCallback?: (message: string, isUser: boolean, isComplete?: boolean) => void;
  private onTranscriptCallback?: (transcript: string, messageId: string, status: 'pending' | 'completed') => void;
  private inputAudioBuffer: Int16Array = new Int16Array(0);
  private language: Language = 'ko';
  private stage: 'pre_surgery' | 'post_surgery' | 'pre_visit' = 'pre_surgery';

  // 🔥 응답 상태 관리
  private currentResponseStarted: boolean = false;
  private currentResponseMessage: string = '';

  constructor() {
    this.client = new RealtimeAPI({
      sessionEndpoint: process.env.REACT_APP_REALTIME_SESSION_ENDPOINT || '/api/realtime-session',
      dangerouslyAllowAPIKeyInBrowser: true,
      debug: false
    });

    // OpenAI Realtime API: 입력/출력 모두 24kHz PCM
    this.wavRecorder = new WavRecorder({ sampleRate: 24000 });
    this.wavStreamPlayer = new WavStreamPlayer({ sampleRate: 24000 });

    this.setupEventHandlers();
  }

  // 🔥 간소화: 의사 성별에 맞는 음성 선택 (고정값)
  private selectVoiceForDoctor(doctor?: Doctor): VoiceType {
    if (!doctor) {
      console.log('🎵 의사 정보 없음, 기본 여성 음성 사용:', DEFAULT_VOICE);
      return DEFAULT_VOICE;
    }

    const selectedVoice = VOICE_MAPPING[doctor.gender] || DEFAULT_VOICE;
    console.log(`🎵 ${doctor.name} 의사 (${doctor.gender}) - 선택된 음성:`, selectedVoice);
    return selectedVoice;
  }

  // 🔥 현재 사용 중인 음성 정보 반환
  public getCurrentVoiceInfo(): { voice: VoiceType; gender: string; doctorName?: string } {
    return {
      voice: this.currentVoice,
      gender: this.doctor?.gender || '여성', // 기본값이 여성 음성이므로
      doctorName: this.doctor?.name
    };
  }

  private setupEventHandlers(): void {
    // 인증 오류 처리
    this.client.on('auth_error', (event: any) => {
      console.error('인증 오류:', event);
      throw new Error('OpenAI API 키가 유효하지 않습니다.');
    });

    // 연결 관련
    this.client.on('server.session.created', (event: any) => {
      console.log('세션 생성됨:', event);
      console.log('🎵 현재 세션 음성 설정:', this.currentVoice);
    });

    // 🔥 음성 인식 관련 - 개선된 처리
    this.client.on('server.conversation.item.input_audio_transcription.completed', (event: any) => {
      console.log('🔥 음성 인식 완료:', event.transcript);
      if (this.onTranscriptCallback && this.pendingMessageId) {
        this.onTranscriptCallback(event.transcript, this.pendingMessageId, 'completed');
        this.pendingMessageId = null;
      }
    });

    this.client.on('server.conversation.item.input_audio_transcription.failed', (event: any) => {
      console.error('음성 인식 실패:', event);
      if (this.onTranscriptCallback && this.pendingMessageId) {
        this.onTranscriptCallback('음성 인식에 실패했습니다.', this.pendingMessageId, 'completed');
        this.pendingMessageId = null;
      }
    });

    // 🔥 응답 시작 이벤트
    this.client.on('server.response.created', (event: any) => {
      console.log('🔥 응답 생성 시작:', event);
      this.currentResponseStarted = true;
      this.currentResponseMessage = '';
    });

    // 🔥 응답 완료 - 확실한 완료 보장
    this.client.on('server.response.output_item.done', (event: any) => {
      console.log('🔥 응답 아이템 완료:', event);
      if (event.item?.status === 'completed') {
        if (event.item.content?.[0]?.text) {
          const text = event.item.content[0].text;
          console.log('🔥 텍스트 응답 완료:', text);
          if (this.onMessageCallback) {
            this.onMessageCallback(text, false, true);
          }
        }
        if (event.item.content?.[0]?.transcript) {
          const transcript = event.item.content[0].transcript;
          console.log('🔥 음성 응답 전사 완료:', transcript);
          if (this.onMessageCallback) {
            this.onMessageCallback(transcript, false, true);
          }
        }
      }
    });

    // 🔥 실시간 텍스트 전사 - 누적 방식으로 개선
    this.client.on('server.response.audio_transcript.delta', (event: any) => {
      if (event.delta) {
        this.currentResponseMessage += event.delta;
        
        if (this.onMessageCallback) {
          this.onMessageCallback(this.currentResponseMessage, false, false);
        }
      }
    });

    // 🔥 응답 완료 시 - 확실한 완료 처리
    this.client.on('server.response.done', (event: any) => {
      console.log('🔥 전체 응답 완료:', event);
      
      if (this.currentResponseMessage.trim() && this.onMessageCallback) {
        // 최종 완료된 메시지 전송
        this.onMessageCallback(this.currentResponseMessage, false, true);
      }
      
      // 상태 리셋
      this.currentResponseStarted = false;
      this.currentResponseMessage = '';
    });

    this.client.on('server.response.audio.delta', (event: any) => {
      if (event.delta) {
        this.wavStreamPlayer.add16BitPCM(event.delta, event.item_id || 'unknown');
      }
    });

    // 에러 처리
    this.client.on('server.error', (event: any) => {
      console.error('서버 오류:', event);
    });

    this.client.on('close', () => {
      this.isFullyConnected = false;
      console.log('연결이 종료되었습니다.');
    });
  }

  // 🔥 의사 정보 및 특이사항에 따른 음성 선택 및 연결
  async connect(
    surgery: Surgery,
    patient: Patient,
    language: Language, // 언어 인자 추가
    doctor?: Doctor,
    specialConditions?: PatientSpecialConditions,
    stage: 'pre_surgery' | 'post_surgery' | 'pre_visit' = 'pre_surgery'
  ): Promise<void> {
    if (this.isConnecting || this.isFullyConnected) {
      console.log('이미 연결 중이거나 연결되어 있습니다.');
      return;
    }
    this.isConnecting = true;
    try {
      this.surgery = surgery;
      this.patient = patient;
      this.doctor = doctor || null;
      this.specialConditions = specialConditions || null;
      this.language = language;
      this.stage = stage;

      // 🔥 의사 성별에 맞는 음성 선택 (간소화)
      this.currentVoice = this.selectVoiceForDoctor(doctor);
      console.log('🎵 최종 선택된 음성:', this.currentVoice);

      // 마이크 권한 확인
      console.log('🎤 마이크 권한 확인 중...');
      try {
        const permissions = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        console.log('마이크 권한 상태:', permissions.state);
        
        if (permissions.state === 'denied') {
          throw new Error('마이크 권한이 거부되었습니다. 브라우저 설정에서 마이크 권한을 허용해주세요.');
        }
      } catch (permError) {
        console.warn('권한 API 지원하지 않음:', permError);
      }

      // 오디오 초기화
      console.log('🔊 오디오 초기화 중...');
      await this.wavRecorder.begin();
      await this.wavStreamPlayer.connect();
      
      // WebSocket 연결
      console.log('🌐 WebSocket 연결 중...');
      await this.client.connect();
      
      // 연결 성공 후 세션 설정
      console.log('⚙️ 세션 설정 중...');
      await this.setupSession();
      
      this.isFullyConnected = true;
       console.log(`🎉 연결 완료! (음성: ${this.currentVoice}, 언어: ${this.language})`);
      
    } catch (error) {
      console.error('연결 실패:', error);
      this.isConnecting = false;
      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  // 🔥 선택된 음성으로 세션 설정
  private async setupSession(): Promise<void> {
    if (!this.surgery || !this.patient) return;

    const instructions = this.createSystemPrompt();

    await this.client.send('session.update', {
      session: {
        output_modalities: ['audio'],
        instructions: instructions,
        voice: this.currentVoice, // 🔥 선택된 OpenAI 음성
        turn_detection: null, // Push-to-Talk 모드
      }
    });

    // 초기 인사 메시지
    const greetingKey = this.doctor ? "openai:greetings.doctor" : "openai:greetings.general";
    const greetingText = tt(greetingKey, { patientName: this.patient.name, doctorName: this.doctor?.name, surgeryName: this.surgery.name }, this.language);

    await this.client.send('conversation.item.create', {
      item: {
        id: 'init_message',
        type: 'message',
        role: 'user',
        content: [{
          type: 'input_text',
          text: greetingText
        }]
      }
    });

    await this.client.send('response.create');
    // 초기 인사 응답 chunk를 받을 준비
    this.wavStreamPlayer.resume();
  }

  // 🔥 수정된 특이사항을 문자열로 변환하는 함수 (4가지만)
  private formatSpecialConditions(conditions: PatientSpecialConditions): string {
    const sections = [];
    
    if (conditions.resectionType) {
      sections.push(`**절제 범위**: ${conditions.resectionType}`);
    }
    
    if (conditions.cancerStage) {
      sections.push(`**암 병기**: ${conditions.cancerStage}`);
    }
    
    if (conditions.reconstructionType) {
      sections.push(`**유방 재건**: ${conditions.reconstructionType}`);
    }
    
    // 기타 특이사항
    if (conditions.otherNotes && conditions.otherNotes.trim()) {
      sections.push(`**기타 특이사항**: ${conditions.otherNotes}`);
    }
    
    // 모름 상태 표시
    if (conditions.unknownStates) {
      const unknownItems = [];
      if (conditions.unknownStates.resectionType) unknownItems.push('절제 범위');
      if (conditions.unknownStates.cancerStage) unknownItems.push('암 병기');
      if (conditions.unknownStates.reconstructionType) unknownItems.push('유방 재건');
      
      if (unknownItems.length > 0) {
        sections.push(`**확인되지 않은 사항**: ${unknownItems.join(', ')}`);
      }
    }
    
    return sections.length > 0 ? sections.join('\n') : '특별한 사항이 없습니다.';
  }

  // 🔥 stage별 추가 시스템 지시문
  private stageInstruction(): string {
    switch (this.stage) {
      case 'post_surgery':
        return `\n\n## 환자 상태 (매우 중요):\n환자는 **이미 수술을 받은 상태**입니다. 수술 후 회복, 통증 관리, 합병증 신호, 일상 복귀 시기, 재활 및 후속 치료 등 **수술 이후 단계**에 초점을 맞춰 답변하세요. 수술 전 준비사항은 다루지 않습니다. 회복 중인 환자의 정서적 안정도 고려해주세요.`;
      case 'pre_visit':
        return `\n\n## 환자 상태 (매우 중요):\n환자는 **아직 병원에 내원하지 않은 상태**입니다. 진료 예약 안내, 첫 내원 시 준비물, 일반적인 진료 흐름, 수술 가능 여부 판단을 위한 검사 종류 등 **내원 전 단계**의 정보 제공에 초점을 맞춰 답변하세요. 수술 세부 사항보다는 환자가 병원에 오기 전에 알아두면 좋을 정보를 친절히 안내해주세요.`;
      case 'pre_surgery':
      default:
        return '';
    }
  }

  // 🔥 수정된 프롬프트 - Patient 타입 변경사항 반영
  private createSystemPrompt(): string {
    if (!this.surgery || !this.patient) return '';

    const sectionsText = this.surgery.sections
      .map((section, index) => `섹션 ${index + 1}: ${section.title}\n${section.content}`)
      .join('\n\n');

    // 🔥 수정된 특이사항 정보 추가 (4가지만)
    const specialConditionsText = this.specialConditions
      ? `\n\n## 환자 유방암 수술 특이사항:\n${this.formatSpecialConditions(this.specialConditions)}\n\n**중요**: 위 특이사항을 반드시 고려하여 환자 맞춤형 답변을 제공하세요. 특히 절제 범위, 암 병기, 재건술 여부 등을 고려한 개별화된 설명을 해주세요.`
      : '\n\n## 환자 유방암 수술 특이사항:\n특별한 사항이 입력되지 않았습니다.';

    const stageText = this.stageInstruction();

    // 🔥 의사 정보에 따른 프롬프트 분기
    const basePrompt = this.doctor
      ? tt('prompts.openai.system_doctor', {
          doctorName: this.doctor.name,
          patientName: this.patient.name,
          surgeryName: this.surgery.name,
          doctorAge: this.doctor.age,
          doctorGender: this.doctor.gender,
          specialConditions: specialConditionsText,
          sections: sectionsText,
          doctorStyle: this.doctor.significant || '친절한 스타일',
          voiceGender: MALE_VOICES.includes(this.currentVoice) ? '남성' : '여성',
          voice: this.currentVoice
        }, this.language)
      : tt('prompts.openai.system_general', {
          patientName: this.patient.name,
          surgeryName: this.surgery.name,
          specialConditions: specialConditionsText,
          sections: sectionsText,
          voiceGender: MALE_VOICES.includes(this.currentVoice) ? '남성' : '여성',
          voice: this.currentVoice
        }, this.language);

    return basePrompt + stageText;
  }

  async sendTextMessage(message: string): Promise<void> {
    // 텍스트 메시지 기능 제거 - 음성만 사용
    console.log('텍스트 메시지는 지원하지 않습니다.');
  }

  // 🔥 현재 응답 중단 기능
  async interruptCurrentResponse(): Promise<void> {
    try {
      console.log('🛑 현재 응답 중단 중...');
      
      // 오디오 스트림 중단
      await this.wavStreamPlayer.interrupt();
      
      // 응답 상태 리셋
      this.currentResponseStarted = false;
      this.currentResponseMessage = '';
      
      // 진행 중인 응답 취소 요청
      await this.client.send('response.cancel');
      
      console.log('✅ 응답 중단 완료');
    } catch (error) {
      console.error('응답 중단 오류:', error);
    }
  }

  // 🔥 맞춤형 섹션 요약 요청 기능
  async requestCustomizedSectionSummary(
    section: Section,
    patient: any,
    language: Language,
    specialConditions?: PatientSpecialConditions
  ): Promise<void> {
    if (!this.isConnected()) {
      throw new Error(tt('services.openai.not_connected', undefined, this.language));
    }

    try {
      console.log('📋 맞춤형 섹션 요약 요청:', section.title);

      this.currentResponseStarted = false;
      this.currentResponseMessage = '';

      // 텍스트 플로우에서는 새 오디오 입력을 만들지 않고 진행 중인 오디오 재생만 중단.
      await this.wavStreamPlayer.interrupt();

      const summaryPrompt = this.createCustomizedSectionSummaryPrompt(section, patient, language, specialConditions);
      
      await this.client.send('conversation.item.create', {
        item: {
          id: `Section_summary_${Date.now()}`,
          type: 'message',
          role: 'user',
          content: [{
            type: 'input_text',
            text: summaryPrompt
          }]
        }
      });

      // 응답 생성 요청
      await this.client.send('response.create', {
        response: {
          output_modalities: ['audio'],
          instructions: tt('prompts.openai.section_summary_instructions', undefined, language)
        }
      });

      // 새 응답 chunk를 받을 준비 (interrupt()에서 거부되던 게이트 해제)
      this.wavStreamPlayer.resume();

      console.log('✅ 맞춤형 섹션 요약 요청 완료');
      
    } catch (error) {
      console.error('맞춤형 섹션 요약 요청 실패:', error);
      throw error;
    }
  }

  // 🔥 수정된 맞춤형 섹션 요약을 위한 프롬프트 생성 (상담체로 변환)
  private createCustomizedSectionSummaryPrompt(
    section: Section,
    patient: any,
    language: Language,
    specialConditions?: PatientSpecialConditions
  ): string {
    const specialConditionsNote = specialConditions ? this.formatSpecialConditions(specialConditions) : '';
    return tt('prompts.openai.section_summary_prompt', {
      sectionTitle: section.title,
      sectionContent: section.content,
      patientName: patient.name,
      specialConditions: specialConditionsNote
    }, language);
  }

  async startRecording(): Promise<void> {
    if (!this.isConnected() || this.isRecording) {
      throw new Error('녹음을 시작할 수 없습니다.');
    }

    console.log("🎤 녹음 시작");
    this.isRecording = true;
    this.inputAudioBuffer = new Int16Array(0);
    
    // 🔥 응답 상태 리셋
    this.currentResponseStarted = false;
    this.currentResponseMessage = '';
    
    // 기존 오디오 버퍼 클리어
    await this.client.send('input_audio_buffer.clear');
    
    // 오디오 스트림 중단
    await this.wavStreamPlayer.interrupt();
    
    // 임시 메시지 ID 생성
    this.pendingMessageId = `pending_${this.messageId++}`;
    
    if (this.onTranscriptCallback) {
      this.onTranscriptCallback('', this.pendingMessageId, 'pending');
    }
    
    // 녹음 시작 - 데이터 수집만 함
    await this.wavRecorder.record((data) => {
      if (data.mono.length > 0 && this.isRecording) {
        // 데이터를 버퍼에만 저장, 실시간 전송하지 않음
        const newLength = this.inputAudioBuffer.length + data.mono.length;
        const newBuffer = new Int16Array(newLength);
        newBuffer.set(this.inputAudioBuffer, 0);
        newBuffer.set(data.mono, this.inputAudioBuffer.length);
        this.inputAudioBuffer = newBuffer;
      }
    });
    
    console.log("✅ 녹음 콜백 설정 완료");
  }

  async stopRecording(): Promise<void> {
    if (!this.isRecording) return;

    console.log("🔴 녹음 중지 및 전송");
    this.isRecording = false;
    
    // 녹음 중지
    await this.wavRecorder.pause();
    
    if (this.inputAudioBuffer.length > 0) {
      console.log(`📤 ${this.inputAudioBuffer.length} 샘플 전송`);

      // 24kHz × 1초 = 24000 샘플. 약 200ms (4800 샘플)씩 분할 전송.
      const CHUNK_SAMPLES = 4800;
      for (let offset = 0; offset < this.inputAudioBuffer.length; offset += CHUNK_SAMPLES) {
        const end = Math.min(offset + CHUNK_SAMPLES, this.inputAudioBuffer.length);
        const chunk = this.inputAudioBuffer.subarray(offset, end);
        const buffer = new ArrayBuffer(chunk.byteLength);
        new Int16Array(buffer).set(chunk);

        await this.client.send('input_audio_buffer.append', {
          audio: this.arrayBufferToBase64(buffer)
        });
      }

      // 오디오 버퍼 커밋 후 명시적으로 응답 생성을 요청한다.
      await this.client.send('input_audio_buffer.commit');

      await this.client.send('response.create', {
        response: {
          output_modalities: ['audio'],
          instructions: tt('prompts.openai.user_question_instructions', undefined, this.language)
        }
      });

      // 새 응답 chunk를 받을 준비
      this.wavStreamPlayer.resume();

    } else {
      console.log('⚠️ 녹음된 오디오가 없습니다');
      if (this.onTranscriptCallback && this.pendingMessageId) {
        this.onTranscriptCallback('녹음된 음성이 없습니다.', this.pendingMessageId, 'completed');
        this.pendingMessageId = null;
      }
    }
    
    this.inputAudioBuffer = new Int16Array(0);
  }

  onMessage(callback: (message: string, isUser: boolean, isComplete?: boolean) => void): void {
    this.onMessageCallback = callback;
  }

  onTranscript(callback: (transcript: string, messageId: string, status: 'pending' | 'completed') => void): void {
    this.onTranscriptCallback = callback;
  }

  isConnected(): boolean {
    return this.isFullyConnected;
  }

  async forceReconnect(): Promise<void> {
    if (!this.surgery || !this.patient) {
      // 🔥 수정: params가 없을 경우 undefined 전달
      throw new Error(tt('services.openai.reconnect_failed', undefined, this.language));
    }

    await this.disconnect();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // 🔥 수정: this.language를 세 번째 인자로 올바르게 전달
    await this.connect(
      this.surgery, 
      this.patient, 
      this.language, 
      this.doctor || undefined, 
      this.specialConditions || undefined
    );
  }

  async disconnect(): Promise<void> {
    this.isRecording = false;
    this.isFullyConnected = false;
    
    // 🔥 상태 리셋
    this.currentResponseStarted = false;
    this.currentResponseMessage = '';
    
    try {
      await this.wavRecorder.end();
      await this.client.disconnect();
    } catch (error) {
      console.error('연결 해제 오류:', error);
    }
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}
